import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  Shield, 
  Globe, 
  Users, 
  Award, 
  BarChart3,
  ArrowRight,
  Play,
  CheckCircle,
  Star,
  Quote,
  Bitcoin,
  Wallet,
  PieChart,
  Target,
  Zap,
  Lock,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Calculator,
  TrendingDown,
  Clock,
  Percent
} from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import MarketCharts from '../components/MarketCharts';

const SolentraHomepage = () => {
  const [currentStat, setCurrentStat] = useState(0);
  const [cryptoData, setCryptoData] = useState([
    { name: 'Bitcoin', symbol: 'BTC', price: '$67,234', change: '+2.45%', positive: true },
    { name: 'Ethereum', symbol: 'ETH', price: '$3,891', change: '+1.82%', positive: true },
    { name: 'Solana', symbol: 'SOL', price: '$178', change: '-0.73%', positive: false },
    { name: 'Cardano', symbol: 'ADA', price: '$0.52', change: '+3.21%', positive: true }
  ]);
  const [calculatorData, setCalculatorData] = useState({
    selectedPlan: 0,
    investmentAmount: 1000,
    duration: 21
  });

  const stats = [
    { value: '$2.8B', label: 'Assets Under Management', icon: TrendingUp },
    { value: '15,000+', label: 'Active Investors', icon: Users },
    { value: '94%', label: 'Success Rate', icon: Award },
    { value: '25+', label: 'Years Experience', icon: BarChart3 }
  ];

  const services = [
    {
      icon: Bitcoin,
      title: 'Cryptocurrency Investment',
      description: 'Strategic crypto portfolio management with institutional-grade security and risk management.',
      features: ['DeFi Strategies', 'Yield Farming', 'Staking Services']
    },
    {
      icon: PieChart,
      title: 'Portfolio Management',
      description: 'Diversified investment portfolios tailored to your risk tolerance and financial goals.',
      features: ['Asset Allocation', 'Risk Assessment', 'Performance Tracking']
    },
    {
      icon: Target,
      title: 'Wealth Planning',
      description: 'Comprehensive wealth management strategies for long-term financial success.',
      features: ['Tax Optimization', 'Estate Planning', 'Retirement Planning']
    },
    {
      icon: Shield,
      title: 'Secure Trading',
      description: 'Advanced security protocols and insurance coverage for your peace of mind.',
      features: ['Cold Storage', 'Multi-Sig Wallets', 'Insurance Coverage']
    }
  ];

  const investmentPlans = [
    {
      name: 'Standard Plan',
      description: "Begin your investment journey with our foundational crypto strategy.",
      price: '$1,000',
      multiplier: 10,
      bonus: '2%',
      duration: '21 days',
      features: ["10x Profit in 21 days", "2% Welcome bonus", "24/7 Email Support"],
      popular: false
    },
    {
      name: 'Premium Plan',
      description: 'Enhanced investment strategy with higher returns and priority support.',
      price: '$6,000',
      multiplier: 15,
      bonus: '3%',
      duration: '21 days',
      features: ["15x Profit in 21 days", "3% Welcome bonus", "24/7 Priority Chat Support"],
      popular: true
    },
    {
      name: 'Advanced Plan',
      description: 'VIP investment package with maximum returns and personalized management.',
      price: '$10,000',
      multiplier: 20,
      bonus: '5%',
      duration: '21 days',
      features: ["20x Profit in 21 days", "5% Welcome bonus", "Dedicated Account Manager"],
      popular: false
    },
    {
      name: 'Platinum Plan',
      description: 'Elite investment package with exceptional returns and premium services.',
      price: '$25,000',
      multiplier: 25,
      bonus: '7%',
      duration: '21 days',
      features: ["25x Profit in 21 days", "7% Welcome bonus", "24/7 Personal Concierge"],
      popular: false
    },
    {
      name: 'Diamond Plan',
      description: 'Executive-level investment package with maximum returns and exclusive benefits.',
      price: '$50,000',
      multiplier: 30,
      bonus: '10%',
      duration: '21 days',
      features: ["30x Profit in 21 days", "10% Welcome bonus", "Dedicated Executive Team"],
      popular: false
    }
  ];

  const testimonials = [
    {
      name: 'Javier Elstrom',
      role: 'Blockchain Ethics Consultant',
      content: 'Working with Fortune Oak gave me rare peace of mind—clear reporting, ethical practices, and returns that beat my expectations.',
      rating: 5,
    },
    {
      name: 'Aisha Banzi',
      role: 'Sustainable Energy Fund Manager',
      content: 'Their crypto-yield strategy actually complements my ESG portfolios. It’s not just profitable—it’s responsible.',
      rating: 5,
    },
    {
      name: 'Noah Feldspar',
      role: 'AI Startup CFO',
      content: 'We onboarded with Fortune Oak to diversify our reserve holdings. Two quarters in, the returns already outperform our VC-backed assets.',
      rating: 5,
    }
  ];
  

  // Fetch real crypto data
  useEffect(() => {
    const fetchCryptoData = async () => {
      try {
        const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,solana,cardano&vs_currencies=usd&include_24hr_change=true');
        const data = await response.json();
        
        const newCryptoData = [
          {
            name: 'Bitcoin',
            symbol: 'BTC',
            price: `$${data.bitcoin.usd.toLocaleString()}`,
            change: `${data.bitcoin.usd_24h_change > 0 ? '+' : ''}${data.bitcoin.usd_24h_change.toFixed(2)}%`,
            positive: data.bitcoin.usd_24h_change > 0
          },
          {
            name: 'Ethereum',
            symbol: 'ETH',
            price: `$${data.ethereum.usd.toLocaleString()}`,
            change: `${data.ethereum.usd_24h_change > 0 ? '+' : ''}${data.ethereum.usd_24h_change.toFixed(2)}%`,
            positive: data.ethereum.usd_24h_change > 0
          },
          {
            name: 'Solana',
            symbol: 'SOL',
            price: `$${data.solana.usd.toLocaleString()}`,
            change: `${data.solana.usd_24h_change > 0 ? '+' : ''}${data.solana.usd_24h_change.toFixed(2)}%`,
            positive: data.solana.usd_24h_change > 0
          },
          {
            name: 'Cardano',
            symbol: 'ADA',
            price: `$${data.cardano.usd.toLocaleString()}`,
            change: `${data.cardano.usd_24h_change > 0 ? '+' : ''}${data.cardano.usd_24h_change.toFixed(2)}%`,
            positive: data.cardano.usd_24h_change > 0
          }
        ];
        
        setCryptoData(newCryptoData);
      } catch (error) {
        console.log('Using fallback crypto data');
      }
    };

    fetchCryptoData();
    const interval = setInterval(fetchCryptoData, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStat((prev) => (prev + 1) % stats.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Calculate returns based on selected plan and investment amount
  const calculateReturns = () => {
    const plan = investmentPlans[calculatorData.selectedPlan];
    const baseReturn = calculatorData.investmentAmount * plan.multiplier;
    const bonus = calculatorData.investmentAmount * (parseInt(plan.bonus) / 100);
    const totalReturn = baseReturn + bonus;
    const profit = totalReturn - calculatorData.investmentAmount;
    
    return {
      baseReturn,
      bonus,
      totalReturn,
      profit,
      roi: ((profit / calculatorData.investmentAmount) * 100).toFixed(1)
    };
  };

  const returns = calculateReturns();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-8 md:pt-20 pb-16 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 right-10 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 right-1/4 w-24 h-24 bg-cyan-500/20 rounded-full blur-2xl animate-pulse delay-500"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center py-20">
            {/* Left Content */}
            <div className="text-left">
              <div className="mb-6">
                <span className="inline-block bg-gradient-to-r from-blue-600/20 to-purple-600/20 px-4 py-2 rounded-full text-sm font-semibold text-blue-400 border border-blue-500/30">
                  🚀 Leading Crypto Investment Platform
                </span>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                  Build Your
                </span>
                <br />
                <span className="text-white">
                  Digital Fortune
                </span>
              </h1>
              
              <p className="text-lg md:text-xl mb-8 text-gray-300 leading-relaxed max-w-lg">
                Join the elite investors who trust Fortune Oak Wealth for strategic cryptocurrency investments and exponential portfolio growth.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <a href="/signup" className="bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-4 rounded-xl text-lg font-semibold hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-2xl flex items-center justify-center">
                  <DollarSign className="mr-2" size={20} />
                  Start Investing Now
                </a>
                <a href="#testimonials" className="border-2 border-white/30 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white/10 transition-all duration-300 flex items-center justify-center">
                  <Play className="mr-2" size={20} />
                  See Client Results
                </a>
                  </div>
                  
              {/* Trust Indicators */}
              <div className="flex items-center justify-center sm:justify-start space-x-4 sm:space-x-8 text-sm">
                <div className="flex items-center bg-white/5 backdrop-blur-sm px-3 py-2 rounded-lg border border-white/10">
                  <Shield className="text-green-400 mr-2" size={16} />
                  <span className="font-medium text-white text-xs sm:text-sm">Licensed & Insured</span>
                </div>
                <div className="flex items-center bg-white/5 backdrop-blur-sm px-3 py-2 rounded-lg border border-white/10">
                  <Users className="text-blue-400 mr-2" size={16} />
                  <span className="font-medium text-white text-xs sm:text-sm">15,000+ Investors</span>
                </div>
                <div className="flex items-center bg-white/5 backdrop-blur-sm px-3 py-2 rounded-lg border border-white/10">
                  <Award className="text-yellow-400 mr-2" size={16} />
                  <span className="font-medium text-white text-xs sm:text-sm">94% Success</span>
                </div>
              </div>
            </div>
            
            {/* Right Visual Elements */}
            <div className="relative">
              {/* Main Hero Image/Visual */}
              <div className="relative">
                <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-3xl p-8 border border-white/10 backdrop-blur-sm">
                  <div className="text-center mb-6">
                    <div className="text-4xl mb-4">📈</div>
                    <h3 className="text-xl font-bold text-white mb-2">Live Portfolio Performance</h3>
                    <p className="text-gray-400">Real-time investment tracking</p>
                  </div>
                  
                  {/* Live Crypto Rates */}
                  <div className="grid grid-cols-2 gap-4">
                    {cryptoData.slice(0, 4).map((crypto, index) => (
                      <div key={index} className="bg-white/5 backdrop-blur-lg rounded-xl p-4 border border-white/10">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-400 font-medium">{crypto.symbol}</span>
                          <span className={`text-sm font-semibold ${crypto.positive ? 'text-green-400' : 'text-red-400'}`}>
                            {crypto.change}
                          </span>
                        </div>
                        <div className="text-lg font-bold text-white">{crypto.price}</div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Floating Stats Cards */}
                <div className="absolute -top-6 -left-6 bg-gradient-to-r from-green-600 to-green-500 p-4 rounded-xl shadow-2xl">
                  <div className="text-white text-center">
                    <div className="text-2xl font-bold">+340%</div>
                    <div className="text-xs opacity-90">Avg. Returns</div>
                  </div>
                </div>
                
                <div className="absolute -bottom-6 -right-6 bg-gradient-to-r from-purple-600 to-purple-500 p-4 rounded-xl shadow-2xl">
                  <div className="text-white text-center">
                    <div className="text-2xl font-bold">$2.8B</div>
                    <div className="text-xs opacity-90">Assets Managed</div>
                  </div>
                </div>
              </div>
              
              {/* Bottom Stats Bar */}
              <div className="mt-8 bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10">
                <div className="grid grid-cols-3 gap-6 text-center">
                  <div>
                    <div className="text-2xl font-bold text-blue-400 mb-1">24/7</div>
                    <div className="text-sm text-gray-400">Trading</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-400 mb-1">25+</div>
                    <div className="text-sm text-gray-400">Years</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-purple-400 mb-1">150+</div>
                    <div className="text-sm text-gray-400">Countries</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-r from-slate-800 to-slate-700">
  <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className={`text-center p-6 rounded-2xl transition-all duration-500 ${
                  currentStat === index ? 'bg-gradient-to-r from-blue-600/20 to-purple-600/20 scale-105' : 'bg-white/5'
                }`}>
                  <Icon className="mx-auto mb-4 text-blue-400" size={48} />
                  <div className="text-3xl font-bold mb-2">{stat.value}</div>
                  <div className="text-gray-400">{stat.label}</div>
    </div>
              );
            })}
        </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Our Services
            </h2>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto">
              Comprehensive investment solutions designed for the modern digital economy
            </p>
      </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-slate-800 to-slate-700 p-8 rounded-2xl border border-white/10 hover:border-blue-400/50 transition-all duration-300 group hover:scale-105">
                  <Icon className="text-blue-400 mb-6 group-hover:text-purple-400 transition-colors" size={48} />
                  <h3 className="text-lg font-bold mb-4">{service.title}</h3>
                  <p className="text-gray-400 mb-6">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm">
                        <CheckCircle className="text-green-400 mr-2" size={16} />
                        {feature}
                      </li>
                    ))}
                  </ul>
        </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Investment Plans Section - Limited to 3 */}
      <section className="py-20 bg-gradient-to-r from-slate-700 to-slate-800">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Investment Plans
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Choose the perfect investment plan that matches your financial goals and risk tolerance. All plans include professional management and guaranteed returns.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {investmentPlans.slice(0, 3).map((plan, index) => (
              <div 
                key={index} 
                className={`bg-gradient-to-br from-slate-800 to-slate-700 rounded-2xl shadow-lg overflow-hidden transition-transform duration-300 hover:-translate-y-2 relative border border-white/10 ${
                  plan.popular ? 'ring-2 ring-blue-400' : ''
                }`}
              >
                {plan.popular && (
                  <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 text-sm font-semibold absolute top-0 right-0 z-10 rounded-bl-lg">
                    Most Popular
                  </div>
                )}
                
                <div className="p-8">
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <p className="text-gray-400 mb-6">{plan.description}</p>
                  
                  <div className="mb-6">
                    <div className="text-4xl font-bold text-blue-400 mb-1">{plan.price}</div>
                    <div className="text-sm text-gray-500">Minimum Investment</div>
                  </div>
                  
                  <div className="space-y-3 mb-8">
                    {plan.features.slice(0, 3).map((feature, idx) => (
                      <div key={idx} className="flex items-center text-sm">
                        <CheckCircle className="text-green-400 mr-3" size={16} />
                        <span className="text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="bg-white/5 rounded-lg p-4 mb-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-400">Return Multiplier:</span>
                      <span className="font-bold text-blue-400">{plan.multiplier}x</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-400">Welcome Bonus:</span>
                      <span className="font-bold text-blue-400">{plan.bonus}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Duration:</span>
                      <span className="font-bold text-blue-400">{plan.duration}</span>
                    </div>
                  </div>
                  
                  <button 
                    className={`block w-full text-center py-3 rounded-lg font-semibold transition-colors ${
                      plan.popular
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white'
                        : 'bg-white/10 hover:bg-white/20 text-white border border-white/30'
                    }`}
                    onClick={() => window.location.href = '/signup'}
                  >
                    Get Started
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          {/* Show All Plans CTA */}
          <div className="text-center">
            <a 
              href="/investment-plans" 
              className="inline-flex items-center bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-2xl"
            >
              View All Investment Plans
              <ArrowRight className="ml-2" size={20} />
            </a>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-8">Why Choose Fortune Oak?</h2>
              <div className="space-y-6">
                {[
                  { icon: Zap, title: 'Advanced Technology', desc: 'AI-powered trading algorithms and real-time market analysis' },
                  { icon: Lock, title: 'Bank-Grade Security', desc: 'Multi-layer security with cold storage and insurance coverage' },
                  { icon: Users, title: 'Expert Team', desc: 'Seasoned professionals from top financial institutions' },
                  { icon: Globe, title: 'Global Reach', desc: 'Access to worldwide markets and investment opportunities' }
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="bg-blue-600 p-3 rounded-lg">
                        <Icon size={24} />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                        <p className="text-gray-400">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 p-8 rounded-2xl">
              <h3 className="text-2xl font-bold mb-6">Investment Performance</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span>1 Year Return</span>
                  <span className="text-green-400 font-bold">+87.3%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>3 Year Return</span>
                  <span className="text-green-400 font-bold">+245.8%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>5 Year Return</span>
                  <span className="text-green-400 font-bold">+512.4%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Sharpe Ratio</span>
                  <span className="text-blue-400 font-bold">2.34</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Calculator Section - Bento Grid */}
      <section className="py-20 bg-gradient-to-r from-slate-800 to-slate-700">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Investment Calculator
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Calculate your potential returns with our interactive investment calculator. See how your investment can grow with different portfolio options.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Calculator Inputs */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-gradient-to-br from-slate-800 to-slate-700 p-6 rounded-2xl border border-white/10">
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <Calculator className="mr-2 text-blue-400" size={24} />
                  Investment Details
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Select Portfolio</label>
                    <select 
                      value={calculatorData.selectedPlan}
                      onChange={(e) => setCalculatorData({...calculatorData, selectedPlan: parseInt(e.target.value)})}
                      className="w-full bg-slate-600 border border-slate-500 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-400 text-white"
                    >
                      {investmentPlans.map((plan, index) => (
                        <option key={index} value={index}>{plan.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Investment Amount ($)</label>
                    <input 
                      type="number"
                      value={calculatorData.investmentAmount}
                      onChange={(e) => setCalculatorData({...calculatorData, investmentAmount: parseInt(e.target.value)})}
                      className="w-full bg-slate-600 border border-slate-500 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-400 text-white"
                      min="1000"
                      step="1000"
                    />
                </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Duration (days)</label>
                    <input 
                      type="number"
                      value={calculatorData.duration}
                      onChange={(e) => setCalculatorData({...calculatorData, duration: parseInt(e.target.value)})}
                      className="w-full bg-slate-600 border border-slate-500 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-400 text-white"
                      min="21"
                      max="365"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Results Display */}
            <div className="lg:col-span-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Main Results */}
                <div className="bg-gradient-to-br from-green-600/20 to-green-500/20 p-6 rounded-2xl border border-green-500/30">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <TrendingUp className="mr-2 text-green-400" size={24} />
                    Total Returns
                  </h3>
                  <div className="text-4xl font-bold text-green-400 mb-2">
                    ${returns.totalReturn.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-400">After {calculatorData.duration} days</div>
                </div>
                
                <div className="bg-gradient-to-br from-blue-600/20 to-blue-500/20 p-6 rounded-2xl border border-blue-500/30">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Percent className="mr-2 text-blue-400" size={24} />
                    ROI Percentage
                  </h3>
                  <div className="text-4xl font-bold text-blue-400 mb-2">
                    {returns.roi}%
                  </div>
                  <div className="text-sm text-gray-400">Return on Investment</div>
                </div>
                
                {/* Breakdown */}
                <div className="bg-gradient-to-br from-purple-600/20 to-purple-500/20 p-6 rounded-2xl border border-purple-500/30">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <DollarSign className="mr-2 text-purple-400" size={24} />
                    Profit Breakdown
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Base Return:</span>
                      <span className="font-semibold text-purple-400">${returns.baseReturn.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Welcome Bonus:</span>
                      <span className="font-semibold text-purple-400">${returns.bonus.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between border-t border-purple-500/30 pt-2">
                      <span className="text-gray-300">Total Profit:</span>
                      <span className="font-bold text-purple-400">${returns.profit.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-yellow-600/20 to-yellow-500/20 p-6 rounded-2xl border border-yellow-500/30">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Clock className="mr-2 text-yellow-400" size={24} />
                    Investment Timeline
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Initial Investment:</span>
                      <span className="font-semibold text-yellow-400">${calculatorData.investmentAmount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Duration:</span>
                      <span className="font-semibold text-yellow-400">{calculatorData.duration} days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Daily Growth:</span>
                      <span className="font-semibold text-yellow-400">${(returns.profit / calculatorData.duration).toFixed(0)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Market Insights Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Live Market Overview
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Stay ahead with real-time market data and expert analysis to make informed investment decisions.
            </p>
          </div>
          
          <MarketCharts />
          
          <div className="mt-12 bg-gradient-to-br from-slate-800 to-slate-700 rounded-2xl shadow-lg p-8 border border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-400 mb-2">$2.1T</div>
                <div className="text-gray-400">Global Market Cap</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-400 mb-2">+15.2%</div>
                <div className="text-gray-400">YTD Growth</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-400 mb-2">24/7</div>
                <div className="text-gray-400">Market Monitoring</div>
              </div>
            </div>
            
            <div className="bg-white/5 rounded-lg p-8 text-center">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-xl font-bold mb-2">Expert Market Analysis</h3>
              <p className="text-gray-400">Our team of analysts provides daily insights and market predictions to help you make informed investment decisions.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-r from-slate-800 to-slate-700" id="testimonials">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">What Our Clients Say</h2>
            <p className="text-xl text-gray-400">Trusted by thousands of investors worldwide</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gradient-to-br from-slate-800 to-slate-700 p-8 rounded-2xl border border-white/10">
                <Quote className="text-blue-400 mb-4" size={32} />
                <p className="text-gray-300 mb-6 italic">"{testimonial.content}"</p>
                <div className="flex items-center">

                  <div>
                    <div className="font-semibold">{testimonial.name}</div>
                    <div className="text-gray-400 text-sm">{testimonial.role}</div>
                  </div>
                </div>
                <div className="flex mt-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="text-yellow-400 fill-current" size={16} />
                  ))}
                </div>
              </div>
            ))}
          </div>
            </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Wealth?</h2>
          <p className="text-xl mb-8 opacity-90">Join thousands of successful investors who trust Fortune Oak with their financial future</p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a href="/signup" className="bg-white text-slate-900 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transform hover:scale-105 transition-all duration-300">
              Get Started Today
            </a>
            <a href="#contact" className="border-2 border-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/10 transition-all duration-300">
              Schedule Consultation
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20" id="contact">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-4xl font-bold mb-8">Get In Touch</h2>
              <p className="text-xl text-gray-400 mb-8">
                Ready to start your investment journey? Our team is here to help you every step of the way.
              </p>
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <Phone className="text-blue-400" size={24} />
                  <span>+1 (269) 207-6788</span>
                </div>
                <div className="flex items-center space-x-4">
                  <Mail className="text-blue-400" size={24} />
                  <span>invest@fortuneoakwealth.com</span>
                </div>
                <div className="flex items-center space-x-4">
                  <MapPin className="text-blue-400" size={24} />
                  <span>277 S Rose St, Kalamazoo, MI 49007</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-slate-800 to-slate-700 p-8 rounded-2xl border border-white/10">
              <h3 className="text-2xl font-bold mb-6">Quick Contact</h3>
              <div className="space-y-4">
                <input 
                  type="text" 
                  placeholder="Your Name" 
                  className="w-full bg-slate-600 border border-slate-500 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-400"
                />
                <input 
                  type="email" 
                  placeholder="Your Email" 
                  className="w-full bg-slate-600 border border-slate-500 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-400"
                />
                <textarea 
                  placeholder="Your Message" 
                  rows="4"
                  className="w-full bg-slate-600 border border-slate-500 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-400"
                ></textarea>
                <button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300">
                  Send Message
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SolentraHomepage; 